import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Home, Search, MessageCircle, ShieldAlert, Send, Flag, Pause, Play, Clock, MapPin, Users, Globe2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Sidebar from "../components/Sidebar";
import api from "../lib/api";
import { toast } from "sonner";

const AUDIENCES = [
  { value: "lgbtqi", label: "LGBTQI+" },
  { value: "femmes", label: "Femmes" },
  { value: "mineurs_accompagnes", label: "Mineur·es accompagné·es" },
  { value: "tous", label: "Toutes victimes" },
];

const LANG_OPTIONS = ["fr", "en", "nl", "de", "es", "ar", "uk", "ru", "pt", "tr", "fa", "ps"];

// ============ TAB 1 — Trouver un refuge ============
function FindRefugeTab({ onOpenThread }) {
  const [city, setCity] = useState("");
  const [audience, setAudience] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!city.trim()) {
      toast.error("Entrez une ville.");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ city: city.trim() });
      if (audience) params.append("audience", audience);
      const r = await api.get(`/refuge/hosts/search?${params.toString()}`);
      setResults(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de recherche.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="refuge-find-tab">
      <div className="bg-amber-50 border-l-4 border-amber-600 p-4 rounded-sm">
        <p className="text-sm text-amber-900 leading-relaxed">
          <strong>Vous êtes en danger ?</strong> Trouvez un hébergement d'urgence proposé par un·e bénévole vérifié·e dans votre ville. Seuls votre quartier et votre ville sont partagés — jamais votre adresse exacte. La conversation reste anonyme tant que vous ne révélez pas votre identité.
        </p>
      </div>

      <form onSubmit={search} className="space-y-3" data-testid="refuge-search-form">
        <div>
          <Label htmlFor="refuge-city">Ville</Label>
          <Input
            id="refuge-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="ex. Bruxelles, Liège, Paris…"
            data-testid="refuge-city-input"
          />
        </div>
        <div>
          <Label htmlFor="refuge-audience">Profil recherché (optionnel)</Label>
          <select
            id="refuge-audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="w-full border border-slate-300 rounded-sm h-10 px-3 text-sm"
            data-testid="refuge-audience-select"
          >
            <option value="">Indifférent</option>
            {AUDIENCES.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="refuge-search-btn">
          <Search className="h-4 w-4 mr-2" /> {loading ? "Recherche…" : "Rechercher un refuge"}
        </Button>
      </form>

      {results && (
        <div data-testid="refuge-search-results">
          <p className="text-sm text-slate-600 mb-3">
            <strong>{results.count}</strong> hôte{results.count > 1 ? "s" : ""} disponible{results.count > 1 ? "s" : ""} à <strong>{results.city}</strong>.
          </p>
          <div className="space-y-3">
            {results.hosts.map((h) => (
              <HostCard key={h.host_anon_id} host={h} onOpenThread={onOpenThread} />
            ))}
            {results.count === 0 && (
              <p className="text-sm text-slate-500 italic">
                Aucun hôte disponible dans cette ville pour le moment. Essayez une ville voisine ou contactez le 1712 (Belgique) / 3919 (France).
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HostCard({ host, onOpenThread }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const startThread = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Écrivez un message d'introduction.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/refuge/thread/start", {
        host_anon_id: host.host_anon_id,
        first_message: message.trim(),
      });
      toast.success("Conversation démarrée. L'hôte sera notifié·e.");
      setOpen(false);
      setMessage("");
      onOpenThread?.(r.data.thread_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white" data-testid={`host-card-${host.host_anon_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-1">Hôte vérifié·e</p>
          <h3 className="font-serif-display text-lg font-medium">
            <MapPin className="h-4 w-4 inline mr-1 text-slate-500" />
            {host.city}{host.neighborhood ? ` · ${host.neighborhood}` : ""}
          </h3>
          {host.short_bio && (
            <p className="text-sm text-slate-700 mt-2 italic">« {host.short_bio} »</p>
          )}
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <span className="bg-slate-100 px-2 py-1 rounded-sm"><Users className="h-3 w-3 inline mr-1" />{host.capacity} pers.</span>
            <span className="bg-slate-100 px-2 py-1 rounded-sm"><Clock className="h-3 w-3 inline mr-1" />{host.max_nights} nuit{host.max_nights > 1 ? "s" : ""} max</span>
            {host.languages?.length > 0 && (
              <span className="bg-slate-100 px-2 py-1 rounded-sm"><Globe2 className="h-3 w-3 inline mr-1" />{host.languages.join(", ")}</span>
            )}
            {host.audience?.map((a) => (
              <span key={a} className="bg-purple-100 text-purple-900 px-2 py-1 rounded-sm">{AUDIENCES.find(x => x.value === a)?.label || a}</span>
            ))}
          </div>
          {host.constraints && (
            <p className="text-xs text-slate-500 mt-2">Conditions : {host.constraints}</p>
          )}
        </div>
      </div>

      {!open ? (
        <Button onClick={() => setOpen(true)} variant="outline" className="mt-3 rounded-sm w-full" data-testid={`contact-host-${host.host_anon_id}`}>
          <MessageCircle className="h-4 w-4 mr-2" /> Démarrer une conversation anonyme
        </Button>
      ) : (
        <form onSubmit={startThread} className="mt-3 space-y-2">
          <Label>Message d'introduction (anonyme)</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Bonjour, je suis dans une situation difficile et je cherche un hébergement pour quelques nuits…"
            rows={4}
            data-testid={`message-input-${host.host_anon_id}`}
          />
          <p className="text-[11px] text-slate-500">
            Votre identité reste cachée. Ne donnez votre numéro ou adresse qu'après plusieurs échanges et signalez tout comportement suspect.
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" disabled={sending} className="flex-1 rounded-sm bg-rose-700 hover:bg-rose-800">
              <Send className="h-4 w-4 mr-2" /> {sending ? "Envoi…" : "Envoyer"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ============ TAB 2 — Devenir hôte ============
function BecomeHostTab({ onSaved }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    city: "",
    neighborhood: "",
    capacity: 1,
    max_nights: 3,
    languages: ["fr"],
    audience: ["tous"],
    constraints: "",
    short_bio: "",
    accept_charter: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/refuge/host/me");
        setProfile(r.data);
        if (r.data.is_host) {
          const h = r.data.host;
          setForm({
            city: h.city || "",
            neighborhood: h.neighborhood || "",
            capacity: h.capacity || 1,
            max_nights: h.max_nights || 3,
            languages: h.languages || ["fr"],
            audience: h.audience || ["tous"],
            constraints: h.constraints || "",
            short_bio: h.short_bio || "",
            accept_charter: true,
          });
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = (field, val) => {
    setForm((f) => {
      const list = f[field] || [];
      return { ...f, [field]: list.includes(val) ? list.filter(x => x !== val) : [...list, val] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_charter) {
      toast.error("Vous devez accepter la charte de sécurité.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/refuge/host/register", form);
      toast.success(r.data.message);
      onSaved?.();
      // Recharger profil
      const me = await api.get("/refuge/host/me");
      setProfile(me.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    } finally { setSubmitting(false); }
  };

  const togglePause = async () => {
    try {
      const action = profile?.host?.status === "paused" ? "resume" : "pause";
      const r = await api.patch(`/refuge/host/${action}`);
      toast.success(action === "pause" ? "Profil mis en pause." : "Profil réactivé.");
      const me = await api.get("/refuge/host/me");
      setProfile(me.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Chargement…</p>;

  if (profile && !profile.is_host && !profile.eligible) {
    return (
      <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-sm" data-testid="refuge-not-eligible">
        <p className="font-medium text-red-900 mb-1">Vous ne pouvez pas encore devenir hôte</p>
        <p className="text-sm text-red-800">{profile.reason}</p>
        <p className="text-xs text-red-700 mt-2">Cette règle protège les personnes en danger contre les profils créés à la dernière minute par des agresseurs potentiels.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="refuge-host-form">
      {profile?.is_host && (
        <div className={`p-4 rounded-sm border ${profile.host.status === "active" ? "bg-green-50 border-green-300 text-green-900" : profile.host.status === "pending" ? "bg-amber-50 border-amber-300 text-amber-900" : profile.host.status === "paused" ? "bg-slate-50 border-slate-300 text-slate-700" : "bg-red-50 border-red-300 text-red-900"}`} data-testid="refuge-host-status">
          <p className="font-medium text-sm">
            Statut actuel : <strong>{profile.host.status === "active" ? "Actif ✓" : profile.host.status === "pending" ? "En cours de validation" : profile.host.status === "paused" ? "En pause" : "Suspendu"}</strong>
          </p>
          {profile.host.status === "active" && (
            <Button type="button" onClick={togglePause} variant="outline" size="sm" className="mt-2 rounded-sm" data-testid="refuge-pause-btn">
              <Pause className="h-3.5 w-3.5 mr-1" /> Mettre en pause
            </Button>
          )}
          {profile.host.status === "paused" && (
            <Button type="button" onClick={togglePause} variant="outline" size="sm" className="mt-2 rounded-sm" data-testid="refuge-resume-btn">
              <Play className="h-3.5 w-3.5 mr-1" /> Réactiver
            </Button>
          )}
        </div>
      )}

      <div className="bg-purple-50 border-l-4 border-purple-700 p-4 rounded-sm text-sm text-purple-900">
        <strong>Charte de sécurité :</strong>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-xs">
          <li>Compte vérifié depuis au moins 30 jours requis (protection contre les profils créés à la hâte).</li>
          <li>Votre vraie adresse n'est <strong>jamais</strong> partagée par l'app — vous décidez de la donner ou non, après plusieurs échanges.</li>
          <li>Messagerie 100 % anonyme côté hébergé·e.</li>
          <li>Toute candidature passe par une validation humaine (48 h max).</li>
          <li>3 signalements = suspension automatique.</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="host-city">Ville</Label>
          <Input id="host-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="host-city-input" />
        </div>
        <div>
          <Label htmlFor="host-neighborhood">Quartier (optionnel)</Label>
          <Input id="host-neighborhood" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} placeholder="ex. Saint-Gilles, Marolles…" data-testid="host-neighborhood-input" />
        </div>
        <div>
          <Label htmlFor="host-capacity">Capacité (1-4 personnes)</Label>
          <Input id="host-capacity" type="number" min="1" max="4" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} required data-testid="host-capacity-input" />
        </div>
        <div>
          <Label htmlFor="host-nights">Nuits maximum</Label>
          <Input id="host-nights" type="number" min="1" max="30" value={form.max_nights} onChange={(e) => setForm({ ...form, max_nights: parseInt(e.target.value) || 1 })} required data-testid="host-nights-input" />
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Profils accueillis</Label>
        <div className="flex flex-wrap gap-2">
          {AUDIENCES.map((a) => (
            <button
              key={a.value}
              type="button"
              onClick={() => toggle("audience", a.value)}
              className={`text-xs px-3 py-1.5 rounded-full border ${form.audience.includes(a.value) ? "bg-purple-700 text-white border-purple-700" : "bg-white border-slate-300 text-slate-700"}`}
              data-testid={`audience-${a.value}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Langues parlées</Label>
        <div className="flex flex-wrap gap-2">
          {LANG_OPTIONS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => toggle("languages", l)}
              className={`text-xs px-3 py-1.5 rounded-full border uppercase ${form.languages.includes(l) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`}
              data-testid={`lang-${l}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="host-constraints">Conditions / contraintes (optionnel)</Label>
        <Input id="host-constraints" value={form.constraints} onChange={(e) => setForm({ ...form, constraints: e.target.value })} placeholder="non-fumeur · pas d'animaux · femmes uniquement…" data-testid="host-constraints-input" />
      </div>

      <div>
        <Label htmlFor="host-bio">Bio anonyme (280 caractères max)</Label>
        <Textarea id="host-bio" maxLength={280} rows={3} value={form.short_bio} onChange={(e) => setForm({ ...form, short_bio: e.target.value })} placeholder="Quelques mots rassurants. Ex. : « Femme 40 ans, j'ai vécu des violences, je veux aider en retour. Maison calme. »" data-testid="host-bio-input" />
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={form.accept_charter}
          onChange={(e) => setForm({ ...form, accept_charter: e.target.checked })}
          className="mt-1"
          data-testid="host-charter-checkbox"
        />
        <span>J'accepte la charte de sécurité et je m'engage à respecter l'anonymat des personnes hébergées.</span>
      </label>

      <Button type="submit" disabled={submitting} className="w-full rounded-sm bg-rose-700 hover:bg-rose-800" data-testid="host-submit-btn">
        {submitting ? "Envoi…" : profile?.is_host ? "Mettre à jour mon profil" : "Soumettre ma candidature"}
      </Button>
    </form>
  );
}

// ============ TAB 3 — Mes conversations ============
function MyThreadsTab({ openThreadId, setOpenThreadId }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const r = await api.get("/refuge/my/threads");
      setThreads(r.data.threads);
    } catch (err) {
      toast.error("Impossible de charger les conversations.");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadThreads(); }, []);

  if (openThreadId) {
    return <ThreadView threadId={openThreadId} onBack={() => { setOpenThreadId(null); loadThreads(); }} />;
  }

  if (loading) return <p className="text-sm text-slate-500">Chargement…</p>;
  if (threads.length === 0) return <p className="text-sm text-slate-500 italic">Aucune conversation pour l'instant.</p>;

  return (
    <div className="space-y-2" data-testid="refuge-threads-list">
      {threads.map((t) => (
        <button
          key={t.thread_id}
          onClick={() => setOpenThreadId(t.thread_id)}
          className="w-full text-left border border-slate-200 rounded-sm p-3 hover:bg-slate-50 transition"
          data-testid={`thread-item-${t.thread_id}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65]">
                {t.my_role === "victim" ? "Avec un hôte" : "Avec une personne hébergée"}
              </p>
              <p className="text-sm font-medium text-slate-900 mt-0.5">
                {t.my_role === "victim" ? t.host_anon_id : t.victim_anon_id}
              </p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-sm ${t.status === "open" ? "bg-green-100 text-green-900" : "bg-slate-100 text-slate-600"}`}>
              {t.status === "open" ? "Active" : "Fermée"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Dernier message : {new Date(t.last_message_at).toLocaleString("fr-BE")}</p>
        </button>
      ))}
    </div>
  );
}

function ThreadView({ threadId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/refuge/thread/${threadId}/messages`);
      setData(r.data);
    } catch (err) {
      toast.error("Impossible de charger la conversation.");
      onBack();
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/refuge/thread/message", { thread_id: threadId, body: body.trim() });
      setBody("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi.");
    } finally { setSending(false); }
  };

  const submitReport = async () => {
    if (!reportReason.trim()) return toast.error("Précisez le motif.");
    try {
      const target = data.my_role === "victim"
        ? data.messages.find(m => m.sender_role === "host")?.sender_anon_id
        : data.messages.find(m => m.sender_role === "victim")?.sender_anon_id;
      await api.post("/refuge/report", {
        target_anon_id: target,
        thread_id: threadId,
        reason: reportReason,
        details: reportDetails,
      });
      toast.success("Signalement envoyé. Merci.");
      setReportOpen(false); setReportReason(""); setReportDetails("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    }
  };

  if (loading || !data) return <p className="text-sm text-slate-500">Chargement…</p>;

  return (
    <div className="space-y-3" data-testid="refuge-thread-view">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="thread-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setReportOpen(true)} className="text-red-600 hover:text-red-700" data-testid="thread-report-btn">
          <Flag className="h-4 w-4 mr-1" /> Signaler
        </Button>
      </div>

      {reportOpen && (
        <div className="bg-red-50 border border-red-300 rounded-sm p-3 space-y-2" data-testid="report-form">
          <Label>Motif du signalement</Label>
          <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full border border-slate-300 rounded-sm h-9 px-2 text-sm" data-testid="report-reason-select">
            <option value="">— Choisir —</option>
            <option value="comportement_predateur">Comportement prédateur / harcelant</option>
            <option value="propos_haineux">Propos haineux / discriminatoires</option>
            <option value="demande_argent">Demande d'argent suspecte</option>
            <option value="identite_pression">Pression pour révéler mon identité</option>
            <option value="autre">Autre</option>
          </select>
          <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder="Détails (facultatif)…" rows={3} data-testid="report-details-input" />
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button size="sm" onClick={submitReport} className="bg-red-600 hover:bg-red-700" data-testid="report-submit-btn">
              Envoyer le signalement
            </Button>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-sm bg-slate-50 max-h-[500px] overflow-y-auto p-3 space-y-2">
        {data.messages.map((m, i) => {
          const mine = m.sender_role === data.my_role;
          return (
            <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-sm px-3 py-2 text-sm ${mine ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-900"}`}>
                <p className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{m.sender_role === "victim" ? "Personne hébergée" : "Hôte"}</p>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className="text-[9px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString("fr-BE")}</p>
              </div>
            </div>
          );
        })}
        {data.messages.length === 0 && <p className="text-xs text-slate-500 italic text-center">Aucun message.</p>}
      </div>

      <form onSubmit={send} className="flex gap-2">
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message anonyme…" disabled={data.status !== "open"} data-testid="message-body-input" />
        <Button type="submit" disabled={sending || data.status !== "open"} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="message-send-btn">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

// ============ PAGE PRINCIPALE ============
export default function Refuge() {
  const navigate = useNavigate();
  const [openThreadId, setOpenThreadId] = useState(null);
  const [tab, setTab] = useState("find");

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="refuge-page">
      <Sidebar />
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/sos")} data-testid="back-to-sos-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> SOS
          </Button>
        </div>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.3em] text-rose-700 mb-2 flex items-center gap-2">
            <Home className="h-3 w-3" /> Touche 13 · Phase 2 · Réseau d'hébergement
          </p>
          <h1 className="font-serif-display text-3xl font-medium text-slate-900">Refuge solidaire</h1>
          <p className="text-sm text-slate-600 mt-2">
            Un réseau d'hôtes bénévoles vérifié·es qui ouvrent leur porte aux victimes d'agression, de violences domestiques ou LGBTQI+phobes. Anonyme · Sécurisé · Modéré.
          </p>
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setOpenThreadId(null); }}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="find" data-testid="tab-find"><Search className="h-3.5 w-3.5 mr-1" />Trouver</TabsTrigger>
            <TabsTrigger value="host" data-testid="tab-host"><ShieldAlert className="h-3.5 w-3.5 mr-1" />Devenir hôte</TabsTrigger>
            <TabsTrigger value="threads" data-testid="tab-threads"><MessageCircle className="h-3.5 w-3.5 mr-1" />Mes échanges</TabsTrigger>
          </TabsList>

          <TabsContent value="find" className="mt-5">
            <FindRefugeTab onOpenThread={(id) => { setOpenThreadId(id); setTab("threads"); }} />
          </TabsContent>
          <TabsContent value="host" className="mt-5">
            <BecomeHostTab onSaved={() => {}} />
          </TabsContent>
          <TabsContent value="threads" className="mt-5">
            <MyThreadsTab openThreadId={openThreadId} setOpenThreadId={setOpenThreadId} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
